// PHOTO-3: a HEIC or HEIF photo has a JPEG preview next to the kept original.
// UPLOAD-PREVIEW-2: local thumbnails and preview kinds. Nothing is uploaded to preview.
import { fileDisplayName } from "./collectDroppedFiles";

/** True when the file has a non-empty preview_url. */
export function hasPreview(file) {
  return typeof file?.preview_url === "string" && file.preview_url.length > 0;
}

/** The path to show: the preview when there is one, else the file itself. */
export function previewPath(file) {
  return hasPreview(file) ? file.preview_url : file?.url;
}

const IMAGE_TYPES = new Set([
  "image/jpeg", "image/png", "image/gif", "image/webp",
  "image/heic", "image/heif", "image/tiff",
]);
const IMAGE_EXT = /\.(heic|heif|jpe?g|png|gif|webp|tiff?)$/;

export const THUMB_SIZE = 192;
export const PDF_THUMB_MAX = 25 * 1024 * 1024;
export const IMAGE_THUMB_MAX = 30 * 1024 * 1024;
export const TEXT_PREVIEW_BYTES = 20480;
const THUMB_JOBS = 3;

export function fileType(file) {
  return String((file && (file.file_type || file.type)) || "").split(";")[0].trim().toLowerCase();
}

export function displayName(file) {
  if (!file) return "file";
  if (file.filename) return String(file.filename);
  return fileDisplayName(file);
}

export function isLocalFile(file) {
  if (!file || file.file_id || file.url || file.filename) return false;
  return typeof file.slice === "function" || typeof file.arrayBuffer === "function";
}

/** image | pdf | text | office | other. .eml is text. docx and xlsx are office. */
export function previewKind(file) {
  const type = fileType(file);
  const name = displayName(file).toLowerCase();
  const imageType = (IMAGE_TYPES.has(type) || type.startsWith("image/")) && !type.includes("dwg");
  if (file?.preview_url || imageType || IMAGE_EXT.test(name)) return "image";
  if (type === "application/pdf" || name.endsWith(".pdf")) return "pdf";
  if (
    type === "message/rfc822" || name.endsWith(".eml")
    || type === "text/csv" || name.endsWith(".csv")
    || type === "text/plain" || name.endsWith(".txt")
  ) return "text";
  if (
    type.includes("wordprocessingml") || name.endsWith(".docx")
    || type.includes("spreadsheetml") || name.endsWith(".xlsx")
  ) return "office";
  return "other";
}

export function formatBytes(bytes) {
  const n = Number(bytes) || 0;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export function matchLocalFile(row, files) {
  const name = displayName(row);
  const size = Number(row && row.size);
  const hits = (files || []).filter((file) => displayName(file) === name && Number(file.size) === size);
  return hits.length === 1 ? hits[0] : null;
}

export function readLocalText(file) {
  const blob = file.slice(0, TEXT_PREVIEW_BYTES);
  if (typeof blob.text === "function") return blob.text();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(reader.error);
    reader.readAsText(blob);
  });
}

let thumbActive = 0;
const thumbWaiters = [];

function enqueueThumb(job) {
  return new Promise((resolve, reject) => {
    const start = () => {
      thumbActive += 1;
      Promise.resolve().then(job).then(resolve, reject).finally(() => {
        thumbActive -= 1;
        const next = thumbWaiters.shift();
        if (next) next();
      });
    };
    if (thumbActive < THUMB_JOBS) start();
    else thumbWaiters.push(start);
  });
}

function objectUrl(file) {
  return URL.createObjectURL(file);
}

async function imageThumb(file, size) {
  if (typeof createImageBitmap !== "function") return objectUrl(file);
  let bitmap = null;
  try {
    try {
      bitmap = await createImageBitmap(file, { resizeWidth: size, resizeQuality: "low" });
    } catch {
      bitmap = await createImageBitmap(file);
    }
  } catch {
    return null;
  }
  try {
    const canvas = document.createElement("canvas");
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext("2d");
    if (!ctx || typeof canvas.toBlob !== "function") return objectUrl(file);
    const iw = bitmap.width || size;
    const ih = bitmap.height || size;
    const scale = Math.max(size / iw, size / ih);
    const dw = iw * scale;
    const dh = ih * scale;
    ctx.drawImage(bitmap, (size - dw) / 2, (size - dh) / 2, dw, dh);
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.8));
    return blob ? URL.createObjectURL(blob) : objectUrl(file);
  } catch {
    return objectUrl(file);
  } finally {
    if (bitmap && bitmap.close) bitmap.close();
  }
}

async function pdfThumb(file, size) {
  const { renderPage } = await import("./pdfPreview");
  const canvas = await renderPage(file, 1, size);
  if (!canvas || typeof canvas.toBlob !== "function") return null;
  const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.8));
  return blob ? URL.createObjectURL(blob) : null;
}

export function makeThumbnail(file, size = THUMB_SIZE) {
  const kind = previewKind(file);
  const bytes = Number(file && file.size) || 0;
  if (kind === "image" && bytes > IMAGE_THUMB_MAX) return Promise.resolve(null);
  if (kind === "pdf" && bytes > PDF_THUMB_MAX) return Promise.resolve(null);
  if (kind !== "image" && kind !== "pdf") return Promise.resolve(null);
  return enqueueThumb(() => (kind === "pdf" ? pdfThumb(file, size) : imageThumb(file, size)));
}

const uploadByteCache = new Map();

export function loadUploadBytes(url, token) {
  const key = `${token || ""}\n${url}`;
  const cached = uploadByteCache.get(key);
  if (cached) return cached;
  const headers = {};
  if (token) headers.Authorization = `Bearer ${token}`;
  let pending;
  pending = fetch(url, { credentials: "include", headers }).then((res) => {
    if (!res.ok) throw new Error("preview");
    return res.arrayBuffer();
  }).finally(() => {
    uploadByteCache.delete(key);
  });
  uploadByteCache.set(key, pending);
  return pending;
}

export function makeUploadPdfThumbnail(url, token, bytes, thumbSize = 40) {
  const n = Number(bytes) || 0;
  if (!url || n > PDF_THUMB_MAX) return Promise.resolve(null);
  return enqueueThumb(async () => {
    const buf = await loadUploadBytes(url, token);
    const { renderPage } = await import("./pdfPreview");
    return renderPage(buf, 1, thumbSize);
  });
}
